import React, { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { userSelector } from "../../slices/user/userSlice";
import { ManifestTableRow } from "../../app/data/manifest/manifestProfiles";
import ManifestDetails from "../manifest/details/manifestDetails";
import ContentContainer from "../../templates/content-container/contentContainer";
import Loading from "../../ui-components/loading/loading";
import "./terminal-insights.scss";
import { ReactComponent as TransportIcon } from "../../images/truck.svg";
import { ReactComponent as DispatchIcon } from "../../images/chart.svg";
import { ReactComponent as ETAIcon } from "../../images/clock_red.svg";
import { ReactComponent as PickupIcon } from "../../images/box_gray.svg";
import { ReactComponent as ResidentialIcon } from "../../images/house_white.svg";
import { ReactComponent as PhoneIcon } from "../../images/phone.svg";
import { ReactComponent as LimitedAccessIcon } from "../../images/location.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faExclamationTriangle, faTruck, faHeadset } from "@fortawesome/free-solid-svg-icons";
import SearchShipmentRequestModel from "../../app/data/tracking/SearchShipmentRequestModel";
import TrackingService from "../../app/data/tracking/trackingService";
import moment from "moment";
import ExceptionService from "../../app/data/exceptions/exceptionsService";
import { ExceptionModel } from "../../app/data/exceptions/models";
import { CreateAppointment } from "../appointment/createAppointment";
import Button, { ButtonThemes } from "ui-components/button/button";
import { apiQueryAdapter } from "app/data/apiQueryAdapter";
import SlideOutSidebar from "../../ui-components/slide-out-sidebar/slideOutSidebar";
import Table from "../../ui-components/table/table";
import XGSDate from "../../ui-components/xgs-date/xgs-date/xgsDate";
import PodDetails from "./components/PodDetails";
import { AppointmentNeed, DashboardData, Exception } from "app/data/terminal-insights/models";
import CustomerServiceDetails from "./components/CustomerServiceDetails";
import { getAppointmentColumns, getExceptionColumns, getManifestTracking } from "./terminalInsightsColumns";
import Tooltip from "./components/Tooltip";
import MetricCard from "./components/MetricCard";
import DashboardCard from "./components/DashboardCard";
import TrendlineModal from "./components/TrendlineModal";
import ManifestsState from "slices/manifest/ManifestsState";
import { getManifests, manifestsSelector, resetManifests } from "slices/manifest/manifestSlice";
import { PAGE_IDS } from "app/data/common/pageIds";


const fetchDashboardData = async ({ terminalId, date }: { terminalId: string; date: string }) => {
  const response = await apiQueryAdapter.query("/ai-analytics/eod/analytics", "GET", {params: {
    "destinationTerminal": terminalId,
    "date": moment(date, 'MM/DD/YYYY').toDate().toApiFormat()
  }});
  return response.data;
}

export const TerminalInsights = () => {
  const userState = useSelector(userSelector);
  const history = useHistory();
  const manifestsState: ManifestsState = useSelector(manifestsSelector);
  const terminalId = String(userState.activeTerminal?.id);
  const [selectedAppointmentProbill, setSelectedAppointmentProbill] = useState<string | null>(null);
  const [selectedManifest, setSelectedManifest] = useState<ManifestTableRow | null>(null);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [displayData, setDisplayData] = useState<'pod' | 'customer-service' | null>(null);
  const [showTrendModal, setShowTrendModal] = useState<boolean>(false);
  const [selectedTrendMetric, setSelectedTrendMetric] = useState<{ 
    key: string; 
    label: string; 
    secondaryKey?: string; 
    secondaryLabel?: string; 
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(moment().format('MM/DD/YYYY'));
  const dispatch = useDispatch();

  const { data: dashboardData, isLoading } = useQuery<DashboardData>({
    queryKey: ["dashboard-data", terminalId, selectedDate],
    queryFn: () => fetchDashboardData({ terminalId, date: selectedDate }),
    retry: false,
    enabled: !!terminalId
  });

  const searchManifests = useCallback(() => {
    let dispatchedDate = moment(new Date()).format("YYYYMMDD")
    let terminal = Number(terminalId)
    dispatch(getManifests({dispatchedDate, terminal}));
  }, [dispatch, terminalId]);

  const { data: appointmentData, isLoading: appointmentsLoading, refetch: refetchAppointments } = useQuery({
    queryKey: ["appointments-needed", terminalId],
    queryFn: () => {
      let request: SearchShipmentRequestModel = {
        destinationId: Number(terminalId),
        appointmentPending: true,
        etaFrom: moment().subtract(1, "month").toDate().toApiFormat(),
        etaTo: moment().toDate().toApiFormat(),
        shipmentGroup: "APPOINTMENT_REQUIRED"
      }
      return TrackingService.getInstance().searchShipments(request)
    },
    retry: false,
    enabled: !!terminalId
  })

  const { data: exceptionsData, isLoading: exceptionsLoading } = useQuery({
    queryKey: ["terminal-exceptions", terminalId],
    queryFn: () => {
      const currentDate = new Date().toApiFormat();
      return ExceptionService.getInstance().getTerminalExceptions(terminalId, currentDate);
    },
    retry: false,
    enabled: !!terminalId
  })

  useEffect(() => {
    searchManifests()
    return () => {
      resetManifests()
    }
  }, [searchManifests])

  useEffect(() => {
    if (dashboardData) {
      mixpanel.track("page_viewed", { page: "terminal_insights" });
    }
  }, [dashboardData])

  const handleDeliveryShowMore = () => {
    mixpanel.track("terminal_insights:show_more", { section: "delivery_snapshot" });
    const baseUrl = '/manifest/manifests';
    
    const url = `/${terminalId}${baseUrl}`;
    history.push(url);
  };

  const handleHomeDepotShowMore = () => {
    mixpanel.track("terminal_insights:show_more", { section: "home_depot" });
    const baseUrl = '/shipments';
    const queryParams = new URLSearchParams();
    queryParams.set('d', terminalId);
    queryParams.set('hdo', 'true');
    queryParams.set('ef', moment().toDate().toApiFormat());
    queryParams.set('et', moment().toDate().toApiFormat());
    
    const url = `/${terminalId}${baseUrl}?${queryParams.toString()}`;
    history.push(url);
  };

  const handleAppointmentsShowMore = () => {
    mixpanel.track("terminal_insights:show_more", { section: "appointment_snapshot" });
    const baseUrl = `/${terminalId}/appointments`;
    const queryParams = new URLSearchParams();

    queryParams.set('ap', 'true');
    queryParams.set('ef', moment().subtract(1, "month").toDate().toApiFormat());
    queryParams.set('et', moment().toDate().toApiFormat());
    const url = `${baseUrl}?${queryParams.toString()}`;
    history.push(url);
  };

  const handlePickupsShowMore = () => {
    mixpanel.track("terminal_insights:show_more", { section: "pickup_snapshot" });
    const baseUrl = `/${terminalId}/pickup/management`;

    const url = `${baseUrl}`;
    history.push(url);
  };

  const handlePodShowMore = () => {
    mixpanel.track("terminal_insights:pod_opened", {});
    setDisplayData('pod');
    setShowSidebar(true);
  };

  const handleCustomerServiceShowMore = () => {
    mixpanel.track("terminal_insights:customer_service_opened", {});
    setDisplayData('customer-service');
    setShowSidebar(true);
  };

  const handleCloseSidebar = () => {
    setShowSidebar(false);
    setDisplayData(null);
  };

  const handleTrendClick = (metricKey: string, metricLabel: string) => {
    console.log('Trend clicked:', metricKey, metricLabel);
    
    // Determine secondary metric info for tracking
    let secondaryKey: string | undefined;
    let secondaryLabel: string | undefined;
    
    // For on-time service metrics, show combined trendline
    if (metricKey === "on_time_service_perc") {
      secondaryKey = "appointment_on_time_service_perc";
      secondaryLabel = "Appointment On-Time %";
      setSelectedTrendMetric({ 
        key: metricKey, 
        label: metricLabel,
        secondaryKey: secondaryKey,
        secondaryLabel: secondaryLabel
      });
    } else if (metricKey === "appointment_on_time_service_perc") {
      secondaryKey = "on_time_service_perc";
      secondaryLabel = "On-Time %";
      setSelectedTrendMetric({ 
        key: metricKey, 
        label: metricLabel,
        secondaryKey: secondaryKey,
        secondaryLabel: secondaryLabel
      });
    } else {
      setSelectedTrendMetric({ key: metricKey, label: metricLabel });
    }
    
    // Track Mixpanel event
    mixpanel.track("Terminal Insights: Trend Clicked", {
      "Terminal ID": terminalId,
      "Terminal Name": userState.activeTerminal?.name,
      "User Role": userState.profile?.role,
      "User Name": `${userState.profile?.firstName} ${userState.profile?.lastName}`,
      "Metric Key": metricKey,
      "Metric Label": metricLabel,
      "Secondary Metric Key": secondaryKey,
      "Secondary Metric Label": secondaryLabel,
      "Selected Date": selectedDate
    });
    
    setShowTrendModal(true);
  };

  const handleCloseTrendModal = () => {
    setShowTrendModal(false);
    setSelectedTrendMetric(null);
  };
  

  // Transform appointment data from ShipmentModel to AppointmentNeed format
  const transformAppointmentData = (data: any): AppointmentNeed[] => {
    if (!data.data?.content) return [];
    
    return data.data.content.map((shipment: any) => ({
      probill: shipment.invoice.invoiceNumber.toString(),
      eta_date: moment(shipment.estimatedDeliveryDate).format('MMM DD, YYYY'),
      consignee: shipment.consignee.info,
      status: shipment.status?.description
    }));
  };

  // Transform exception data from ExceptionModel to Exception format
  const transformExceptionData = (data: any): Exception[] => {
    if (!data?.data) return [];
    
    return data.data.map((exception: ExceptionModel) => ({
      probill: exception.probillNumber, // Using probill as consignee for now
      description: exception.description,
      addedDate: exception.addedDate?.toUiDateFormat() || "",
      reason: exception.type
    }));
  };

  const appointmentsNeeded = appointmentData ? transformAppointmentData(appointmentData) : [];
  const exceptionsData_transformed = exceptionsData ? transformExceptionData(exceptionsData) : [];

  // Enhanced handlers with tracking
  const handleManifestRowClick = (row: ManifestTableRow) => {
    mixpanel.track("terminal_insights:manifest_row_clicked", { manifest_number: row.manifestNumber });
    setSelectedManifest(row);
  };

  const handleAppointmentRowClick = (row: any) => {
    setSelectedAppointmentProbill(row.probill);
  };

  const handleAppointmentSuccess = () => {
    mixpanel.track("terminal_insights:appointment_created", { probill: selectedAppointmentProbill, source: "terminal_insights" });
    refetchAppointments();
  };

  return (
    <div className="daily-overview">
      <ContentContainer 
        mods={{ "no-padding": true, "blue-highlight": true }} 
        title="Daily overview"
        pageId={PAGE_IDS.TERMINAL_INSIGHTS}
      >
        {isLoading && <Loading isLoading />}
        {!isLoading && !dashboardData && <div className="daily-overview__no-data">No data available</div>}
        {!isLoading && !!dashboardData && (
          <>
            <div className="daily-overview__header">
              <div className="daily-overview__greeting">
                <div className="daily-overview__greeting__text">
                  {new Date().getHours() >= 18 ? 'Good evening' : new Date().getHours() >= 12 ? 'Good afternoon' : 'Good morning'}, {userState.profile?.firstName} 👋
                </div>
                {dashboardData.last_updated && <div className="daily-overview__last-updated">
                  Analytics last updated: {moment(dashboardData.last_updated).format('MMM DD, YYYY h:mm A')} • Updates hourly
                </div>}
              </div>
              <div className="daily-overview__date-picker">
                <XGSDate
                  value={selectedDate}
                  onDateChange={setSelectedDate}
                  onChange={() => {}}
                  placeholder="Select date"
                  minDate={moment().subtract(1, 'month').toDate()}
                  maxDate={new Date()}
                  disableWeekends={true}
                  className="daily-overview__date-input"
                />
              </div>
            </div>

            {/* Main Dashboard Cards */}
            <div className="daily-overview__main-cards">
              <DashboardCard
                icon={<DispatchIcon />}
                title="Delivery Snapshot"
                totalValue={dashboardData.analytics.general_summary.summary.total_shipments}
                totalLabel="shipments"
                metrics={[
                  { label: "Delivered", value: dashboardData.analytics.general_summary.summary.delivered_count },
                  { label: "Out for delivery", value: dashboardData.analytics.general_summary.summary.out_for_delivery_count },
                  { label: "Scheduled today", value: dashboardData.analytics.general_summary.summary.scheduled_today_count },
                  { 
                    label: "Overdue shipments", 
                    value: dashboardData.analytics.general_summary.summary.late_count,
                    trend: dashboardData.trends?.late_count?.vs_yesterday ? {
                      vs_yesterday: dashboardData.trends.late_count?.vs_yesterday,
                      vs_last_week: dashboardData.trends.late_count?.vs_last_week,
                      metricKey: "late_count",
                      higher_is_better: dashboardData.trends.late_count?.higher_is_better
                    } : undefined
                  },
                ]}
                onShowMore={handleDeliveryShowMore}
                info={dashboardData.analytics.general_summary.description}
                onTrendClick={handleTrendClick}
              />
              
              <DashboardCard
                icon={<TransportIcon />}
                title="Home Depot"
                totalValue={dashboardData.analytics.home_depot_analysis.summary.total_shipments}
                totalLabel="shipments"
                metrics={[
                  { 
                    label: "Revenue", 
                    value: `$${dashboardData.analytics.home_depot_analysis.summary.total_revenue.toFixed(2)}`,
                    trend: dashboardData.trends?.home_depot_revenue?.vs_yesterday ? {
                      vs_yesterday: dashboardData.trends.home_depot_revenue.vs_yesterday,
                      vs_last_week: dashboardData.trends.home_depot_revenue?.vs_last_week,
                      metricKey: "home_depot_revenue",
                      higher_is_better: dashboardData.trends.home_depot_revenue.higher_is_better
                    } : undefined
                  },
                  { 
                    label: "Appt. Booked Rate",
                    value: `${dashboardData.trends?.home_depot_appointment_percentage?.current.toFixed(1) || '0.0'}%`,
                    trend: dashboardData.trends?.home_depot_appointment_percentage?.vs_yesterday ? {
                      vs_yesterday: dashboardData.trends.home_depot_appointment_percentage.vs_yesterday,
                      vs_last_week: dashboardData.trends.home_depot_appointment_percentage?.vs_last_week,
                      metricKey: "home_depot_appointment_booked_perc",
                      higher_is_better: dashboardData.trends.home_depot_appointment_percentage.higher_is_better
                    } : undefined
                  },
                  { 
                    label: "Overdue shipments",
                    value: dashboardData.analytics.home_depot_analysis.summary.late_count,
                    trend: dashboardData.trends?.home_depot_late_count?.vs_yesterday ? {
                      vs_yesterday: dashboardData.trends.home_depot_late_count.vs_yesterday,
                      vs_last_week: dashboardData.trends.home_depot_late_count?.vs_last_week,
                      metricKey: "home_depot_late_count",
                      higher_is_better: dashboardData.trends.home_depot_late_count.higher_is_better
                    } : undefined
                  },
                  { label: "Appointments booked", value: dashboardData.analytics.home_depot_analysis.summary.booked_count},
                  { label: "Appointments required", value: dashboardData.analytics.home_depot_analysis.summary.appointment_required_count},
                  { label: "Out for delivery", value: dashboardData.analytics.home_depot_analysis.summary.out_for_delivery_count },
                  { label: "Scheduled today", value: dashboardData.analytics.home_depot_analysis.summary.scheduled_today_count },
                ]}
                onShowMore={handleHomeDepotShowMore}
                info={["Delivery snapshot for shipments with payor Home Depot"]}
                onTrendClick={handleTrendClick}
              />
              
              <DashboardCard
                icon={<ETAIcon />}
                title="Appointment Snapshot"
                totalValue={dashboardData.analytics.appointment_analysis.summary.required_count}
                totalLabel="required"
                metrics={[
                  { 
                    label: "Booked %", 
                    value: `${dashboardData.trends?.appointment_percentage?.current.toFixed(1) || '0.0'}%`,
                    trend: dashboardData.trends ? {
                      vs_yesterday: dashboardData.trends?.appointment_percentage?.vs_yesterday || null,
                      vs_last_week: dashboardData.trends?.appointment_percentage?.vs_last_week,
                      metricKey: "appointment_booked_perc",
                      higher_is_better: dashboardData.trends.appointment_percentage?.higher_is_better
                    } : undefined
                  },
                  { label: "Booked", value: dashboardData.analytics.appointment_analysis.summary.booked_count },
                  { label: "Missing", value: dashboardData.analytics.appointment_analysis.summary.missing_count },
                ]}
                onShowMore={handleAppointmentsShowMore}
                info={dashboardData.analytics.appointment_analysis.description}
                onTrendClick={handleTrendClick}
              />
              
              <DashboardCard
                icon={<PickupIcon />}
                title="Pickup Snapshot"
                totalValue={dashboardData.analytics.pickup_analysis.summary.total_pickups}
                totalLabel="pickups"
                metrics={[
                  { 
                    label: "Total Pickups", 
                    value: dashboardData.analytics.pickup_analysis.summary.total_pickups,
                    trend: dashboardData.trends?.total_pickups?.vs_yesterday ? {
                      vs_yesterday: dashboardData.trends.total_pickups.vs_yesterday,
                      vs_last_week: dashboardData.trends.total_pickups?.vs_last_week,
                      metricKey: "total_pickups",
                      higher_is_better: dashboardData.trends.total_pickups.higher_is_better
                    } : undefined
                  },
                  { 
                    label: "Missed", 
                    value: dashboardData.analytics.pickup_analysis.summary.missed_pickups,
                    trend: dashboardData.trends?.missed_pickups?.vs_yesterday ? {
                      vs_yesterday: dashboardData.trends.missed_pickups.vs_yesterday,
                      vs_last_week: dashboardData.trends.missed_pickups?.vs_last_week,
                      metricKey: "missed_pickups",
                      higher_is_better: dashboardData.trends.missed_pickups.higher_is_better
                    } : undefined
                  },
                  { label: "Unassigned", value: dashboardData.analytics.pickup_analysis.summary.unassigned_pickups },
                  { label: "Assigned", value: dashboardData.analytics.pickup_analysis.summary.assigned_pickups },
                  { label: "Rescheduled", value: dashboardData.analytics.pickup_analysis.summary.rescheduled_pickups },
                ]}
                onShowMore={handlePickupsShowMore}
                info={dashboardData.analytics.pickup_analysis.description}
                onTrendClick={handleTrendClick}
              />
              
              <DashboardCard
                icon={<FontAwesomeIcon icon={faHeadset} />}
                title="Customer Service"
                totalValue={dashboardData.analytics.customer_service_analysis.summary.total_calls}
                totalLabel="calls"
                metrics={[
                  {
                    label: "Total Calls",
                    value: dashboardData.analytics.customer_service_analysis.summary.total_calls,
                    trend: dashboardData.trends ? {
                      vs_yesterday: dashboardData.trends.customer_service_volume?.vs_yesterday || null,
                      vs_last_week: dashboardData.trends.customer_service_volume?.vs_last_week,
                      metricKey: "customer_service_calls",
                      higher_is_better: dashboardData.trends.customer_service_volume?.higher_is_better
                    } : undefined
                  },
                  ...Object.entries(dashboardData.analytics.customer_service_analysis.summary.types_breakdown)
                    .sort(([,a], [,b]) => Number(b) - Number(a))
                    .slice(0, 4)
                    .map(([type, count]) => ({
                      label: type,
                      value: Number(count)
                    }))
                ]}
                onShowMore={handleCustomerServiceShowMore}
                info={dashboardData.analytics.customer_service_analysis.description}
                onTrendClick={handleTrendClick}
              />

              {dashboardData.analytics.top_5_customers_analysis && (
                <DashboardCard
                  icon={<FontAwesomeIcon icon={faTruck} />}
                  title="Top Customers"
                  totalValue={dashboardData.analytics.top_5_customers_analysis.summary.total_customers}
                  totalLabel="customers"
                  metrics={[
                    ...dashboardData.analytics.top_5_customers_analysis.summary.top_customers.map((customer) => ({
                      label: customer.customer_name,
                      value: Number.isFinite(customer.total_freight) ? "$" + customer.total_freight.toFixed(2) : "$0.00"
                    })),
                    {
                      label: "Other Payors",
                      value: `$${dashboardData.analytics.top_5_customers_analysis.summary.other_payors_revenue.toFixed(2)}`
                    }
                  ]}
                  info={dashboardData.analytics.top_5_customers_analysis.description}
                />
              )}

              {dashboardData.analytics.on_time_service_analysis && !!dashboardData.analytics.on_time_service_analysis.summary.total_delivered && (
                <DashboardCard
                  icon={<FontAwesomeIcon icon={faCalendarAlt} />}
                  title="On-Time Service"
                  totalValue={dashboardData.analytics.on_time_service_analysis.summary.total_delivered}
                  totalLabel="delivered"
                  metrics={[
                    { 
                      label: "On Time %", 
                      value: `${dashboardData.trends?.on_time_service_percentage?.current.toFixed(1) || dashboardData.analytics.on_time_service_analysis.summary.on_time_percentage.toFixed(1)}%`,
                      trend: dashboardData.trends?.on_time_service_percentage?.vs_yesterday ? {
                        vs_yesterday: dashboardData.trends.on_time_service_percentage.vs_yesterday || null,
                        vs_last_week: dashboardData.trends.on_time_service_percentage?.vs_last_week,
                        metricKey: "on_time_service_perc",
                        higher_is_better: dashboardData.trends.on_time_service_percentage.higher_is_better
                      } : undefined
                    },
                    { 
                      label: "Appointment %", 
                      value: `${dashboardData.trends?.appointment_on_time_service_percentage?.current.toFixed(1) || dashboardData.analytics.appointment_service_analysis.summary.appointment_on_time_percentage.toFixed(1)}%`,
                      trend: dashboardData.trends?.appointment_on_time_service_percentage?.vs_yesterday ? {
                        vs_yesterday: dashboardData.trends.appointment_on_time_service_percentage.vs_yesterday || null,
                        vs_last_week: dashboardData.trends.appointment_on_time_service_percentage?.vs_last_week,
                        metricKey: "appointment_on_time_service_perc",
                        higher_is_better: dashboardData.trends.appointment_on_time_service_percentage.higher_is_better
                      } : undefined
                    },
                    { 
                      label: "Late Count", 
                      value: dashboardData.analytics.on_time_service_analysis.summary.late_count
                    },
                    { 
                      label: "Weather Delays", 
                      value: dashboardData.analytics.on_time_service_analysis.summary.weather_delay_count
                    }
                  ]}
                  info={dashboardData.analytics.on_time_service_analysis.description}
                  onTrendClick={handleTrendClick}
                />
              )}
            </div>
            
            {/* Accessorial Metric Cards */}
            <div className="daily-overview__metric-cards">
              <MetricCard
                icon={<ResidentialIcon />}
                title="Residential"
                value={dashboardData.analytics.accessorial_analysis.summary.residential_delivery_count}
                subtitle="Deliveries"
                color="#1E40AF"
              />
              <MetricCard
                icon={<PhoneIcon />}
                title="Notify Fee"
                value={dashboardData.analytics.accessorial_analysis.summary.notify_fee_count}
                subtitle="Shipments"
                color="#27AE60"
              />
              <MetricCard
                icon={<TransportIcon />}
                title="Driver Collect"
                value={dashboardData.analytics.accessorial_analysis.summary.driver_collect_count}
                subtitle="Shipments"
                color="#9B51E0"
              />
              <MetricCard
                icon={<LimitedAccessIcon />}
                title="Limited Access"
                value={dashboardData.analytics.accessorial_analysis.summary.limited_access_count}
                subtitle="Deliveries"
                color="#F2994A"
              />
            </div>

            {/* POD Snapshot - Last 30 Days */}
            <div className="daily-overview__pod-section">
              <div className="daily-overview__pod-header">
                <h3>
                  POD Snapshot (Last 30 Days)
                  <Button 
                    theme={ButtonThemes.transparent} 
                    className="daily-overview__pod-show-more"
                    onClick={handlePodShowMore}
                  >
                    Show more ↗
                  </Button>
                </h3>
                <div className="daily-overview__pod-stats">
                  <span>Delivered without POD: <strong>{dashboardData.analytics.delivered_no_pod_analysis.summary.total_probills}</strong></span>
                  <span>Less than 1 week: <strong>{dashboardData.analytics.delivered_no_pod_analysis.summary.less_than_1_week}</strong></span>
                  <span>Less than 2 weeks: <strong>{dashboardData.analytics.delivered_no_pod_analysis.summary.less_than_2_weeks}</strong></span>
                  <span>Less than 1 month: <strong>{dashboardData.analytics.delivered_no_pod_analysis.summary.less_than_1_month}</strong></span>
                  <span>Average age: <strong>{Math.round(dashboardData.analytics.delivered_no_pod_analysis.summary.average_age_days)} days</strong></span>
                  <Tooltip tooltips={dashboardData.analytics.delivered_no_pod_analysis.description} />
                </div>
              </div>
            </div>

            {/* Tables Section */}
            <div className="daily-overview__tables-header">
              <h3>Real-time Data</h3>
              <div className="daily-overview__tables-subtitle">
                The tables below show live data and update in real-time
              </div>
            </div>
            <div className="daily-overview__tables">
              <div className="daily-overview__table-section">
                <div className="daily-overview__table-header">
                  <div className="daily-overview__table-title">
                    <div className="daily-overview__table-icon-box daily-overview__table-icon-box--manifest">
                      <FontAwesomeIcon icon={faTruck} />
                    </div>
                    <span>Manifests ({manifestsState.manifestsStatuses ? Object.values(manifestsState.manifestsStatuses).length : 0})</span>
                    {/* No description available for manifests table */}
                  </div>
                </div>
                <div className="daily-overview__table-container">
                  {manifestsState.fetch_was_started ? (
                    <div className="daily-overview__loading">Loading manifests...</div>
                  ) : (
                    <Table
                      columns={getManifestTracking()}
                      data={manifestsState.manifestsStatuses ? Object.values(manifestsState.manifestsStatuses) : []}
                      onRowClicked={handleManifestRowClick}
                      responsive
                      enableRowSelection={false}
                      cursorPointer
                      minTableHeight={350}
                      noResultsText="No manifests for today"
                      infiniteScroll
                      strictMinTableHeight
                    />
                  )}
                </div>
              </div>

              <div className="daily-overview__table-section">
                <div className="daily-overview__table-header">
                  <div className="daily-overview__table-title">
                    <div className="daily-overview__table-icon-box daily-overview__table-icon-box--exception">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <span>Exceptions ({exceptionsData_transformed.length})</span>
                    {/* No description available for exceptions table */}
                  </div>
                </div>
                <div className="daily-overview__table-container">
                  {exceptionsLoading ? (
                    <div className="daily-overview__loading">Loading exceptions...</div>
                  ) : (
                    <Table
                      columns={getExceptionColumns()}
                      data={exceptionsData_transformed}
                      responsive
                      enableRowSelection={false}
                      minTableHeight={350}
                      strictMinTableHeight
                      noResultsText="No exceptions for today"
                      infiniteScroll
                      infiniteScrollHasNext={false}
                      infiniteScrollLoading={false}
                    />
                  )}
                </div>
              </div>

              <div className="daily-overview__table-section">
                <div className="daily-overview__table-header">
                  <div className="daily-overview__table-title">
                    <div className="daily-overview__table-icon-box daily-overview__table-icon-box--appointment">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                    </div>
                    <span>Needs appointment ({appointmentsNeeded.length})</span>
                    {/* No description available for appointments table */}
                  </div>
                </div>
                <div className="daily-overview__table-container">
                  {appointmentsLoading ? (
                    <div className="daily-overview__loading">Loading appointments...</div>
                  ) : (
                    <Table
                      columns={getAppointmentColumns()}
                      data={appointmentsNeeded}
                      onRowClicked={handleAppointmentRowClick}
                      responsive
                      enableRowSelection={false}
                      cursorPointer
                      minTableHeight={350}
                      highlightRow={selectedAppointmentProbill ? appointmentsNeeded.findIndex((appointment) => appointment.probill === selectedAppointmentProbill) : null}
                      infiniteScroll
                      infiniteScrollHasNext={false}
                      infiniteScrollLoading={false}
                      strictMinTableHeight
                    />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </ContentContainer>
      {!!selectedAppointmentProbill && (
        <CreateAppointment
          probillNumber={Number(selectedAppointmentProbill)}
          onClose={() => setSelectedAppointmentProbill(null)}
          onSuccess={handleAppointmentSuccess}
        />
      )}
      {selectedManifest && (
        <ManifestDetails
          manifest={selectedManifest}
          show={!!selectedManifest?.manifestNumber}
          onClose={() => setSelectedManifest(null)}
        />
      )}
      <SlideOutSidebar
        header={displayData === 'pod' ? 'POD Details (Last 30 Days)' : 'Customer Service Calls'}
        show={showSidebar}
        onClose={handleCloseSidebar}
        // size="large"
      >
        {displayData === 'pod' && dashboardData && (
          <PodDetails data={dashboardData.analytics.delivered_no_pod_analysis} />
        )}
        {displayData === 'customer-service' && dashboardData && (
          <CustomerServiceDetails data={dashboardData.analytics.customer_service_analysis} />
        )}
      </SlideOutSidebar>
        {selectedTrendMetric && (
          <TrendlineModal
            isOpen={showTrendModal}
            onClose={handleCloseTrendModal}
            terminalId={terminalId}
            metric={selectedTrendMetric.key}
            metricLabel={selectedTrendMetric.label}
            secondaryMetric={selectedTrendMetric.secondaryKey}
            secondaryMetricLabel={selectedTrendMetric.secondaryLabel}
            selectedDate={selectedDate}
          />
        )}
    </div>
  );
};

export default TerminalInsights;
